import { BRAND } from "@/lib/brand";

// Static legal page — Privacy & Data Protection (PDPA). Content is fixed copy;
// not CMS-managed. Linked from the footer.
const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-serif text-2xl md:text-3xl text-ink mb-3">{title}</h2>
    <div className="space-y-3 text-ink_soft/85 font-light leading-relaxed">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-ink_soft/85 font-light leading-relaxed">
    {items.map((t) => <li key={t}>{t}</li>)}
  </ul>
);

export default function PrivacyPage() {
  return (
    <div data-testid="page-privacy" className="bg-cream">
      <div className="container-page py-16 md:py-24 max-w-3xl">
        <div className="eyebrow text-bronze mb-2">Our Commitment to Your Privacy</div>
        <h1 className="font-serif text-4xl md:text-5xl text-ink leading-tight">
          Privacy and Data Protection
        </h1>

        <p className="mt-8 text-ink_soft/85 font-light leading-relaxed">
          We typically do not collect your personal data unless (a) it is voluntarily provided to us by you
          or through a third party authorized by you (your “authorized representative”) after (i) you (or your
          authorized representative) have been informed of the purposes for which the data is collected, and
          (ii) you (or your authorized representative) have given written consent for the collection and use of
          your personal data for those purposes, or (b) the collection and use of personal data without consent
          is permitted or required by the PDPA or other applicable laws. We will obtain your consent before
          collecting any additional personal data or before using your personal data for a purpose that has not
          been disclosed to you (unless permitted or authorized by law).
        </p>

        <Section title="Introduction">
          <p>
            We value your privacy and are committed to protecting your personal data. This Privacy and Data
            Protection section outlines how we collect, use, and safeguard your personal information in
            compliance with Singapore’s Personal Data Protection Act (PDPA).
          </p>
        </Section>

        <Section title="Collection of Personal Data">
          <p>We collect personal data that you provide to us when you:</p>
          <Bullets items={[
            "Register on our website",
            "Subscribe to our newsletter",
            "Fill out a contact form",
            "Make inquiries or request information about our services",
          ]} />
        </Section>

        <Section title="Use of Personal Data">
          <p>We use your personal data to:</p>
          <Bullets items={[
            "Provide and improve our services",
            "Communicate with you",
            "Process your inquiries and requests",
            "Send you updates and promotional materials",
            "Comply with legal obligations",
          ]} />
        </Section>

        <Section title="Disclosure of Personal Data">
          <p>We may disclose your personal data to:</p>
          <Bullets items={[
            "Our employees, agents, and third-party service providers",
            "Regulatory authorities as required by law",
            "Other parties with your consent",
          ]} />
        </Section>

        <Section title="Protection of Personal Data">
          <p>
            We implement reasonable security measures to protect your personal data from unauthorized access,
            use, or disclosure. These measures include:
          </p>
          <Bullets items={[
            "Minimised collection of personal data",
            "Regular site maintenance, security review and testing performed regularly",
          ]} />
          <p>
            There is no method of transmission over the Internet or method of electronic storage that ensures
            complete security. Therefore, you should be aware of the risk. However, we strive to protect the
            security of your information and are constantly reviewing and enhancing our information security
            measures.
          </p>
        </Section>

        <Section title="Access and Correction of Personal Data">
          <p>
            You have the right to access and correct your personal data held by us. If you wish to do so, please
            contact us in writing.
          </p>
        </Section>

        <Section title="Withdrawal of Consent">
          <p>You may withdraw your consent to the use of your personal data at any time by contacting us.</p>
          <p>However, withdrawing consent may affect our ability to provide you with certain services.</p>
        </Section>

        <Section title="Amendments to the Privacy Policy">
          <p>
            We may update this Privacy and Data Protection section from time to time. Any changes will be posted
            on this page, and we encourage you to review it regularly.
          </p>
        </Section>

        <Section title="Disclaimer on the Collection of Personal Data">
          <p>
            By providing your personal data to us, you acknowledge that you have read and understood this Privacy
            and Data Protection section. You also consent to the collection, use, and disclosure of your personal
            data as described herein. If you do not agree with any part of this section, please do not provide
            your personal data to us.
          </p>
        </Section>

        <Section title="Contact Information">
          <p>
            If you have any questions or concerns about our privacy practices, please contact our Data Protection
            Officer at:
          </p>
          <p>
            Email:{" "}
            <a href={`mailto:${BRAND.email}`} className="text-bronze hover:text-bronze_dark underline" data-testid="privacy-contact-email">
              {BRAND.email}
            </a>
          </p>
          <p>Thank you for trusting us with your personal data.</p>
          <p>We are committed to protecting your privacy and ensuring the security of your information.</p>
        </Section>
      </div>
    </div>
  );
}
